'use client';

import { getAssetSrcSet } from '@/lib/assets';
import { ASSET_BASE_URL } from '@/lib/assets/getStaticAssetUrl';
import { useManifest } from '@/lib/manifest-context';
import type { Manifest } from '@/lib/manifest-context';

export type R2ImagePath =
  | string
  | { url?: string; path?: string; hashedName?: string }
  | null
  | undefined;

interface R2ImageProps {
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';

  path?: R2ImagePath;
  alt?: string;
  className?: string;
  containerClassName?: string;
  sizes?: string;
  width?: number;
  height?: number;
  loading?: 'lazy' | 'eager';
  priority?: boolean;
  style?: React.CSSProperties;
  onLoad?: React.ReactEventHandler<HTMLImageElement>;
  onError?: React.ReactEventHandler<HTMLImageElement>;
  fetchPriority?: 'high' | 'low' | 'auto';
  unoptimized?: boolean;
}

const IMAGE_EXTENSION_REGEX = /\.(avif|webp|png|jpe?g|gif|svg)$/i;

const isFullUrl = (path: string) =>
  typeof path === 'string' &&
  (path.startsWith('http://') || path.startsWith('https://'));

const hasImageExtension = (path: string) =>
  typeof path === 'string' && IMAGE_EXTENSION_REGEX.test(path);

const addDefaultWebpExtension = (path: string) => {
  if (!path || typeof path !== 'string') return '';
  if (hasImageExtension(path)) return path;
  return `${path}.webp`;
};

const normalizePublicPath = (path: string) => {
  if (!path || typeof path !== 'string') return '';

  if (path.startsWith('/public/')) {
    return path.replace(/^\/public/, '');
  }

  if (path.startsWith('public/')) {
    return `/${path.replace(/^public\//, '')}`;
  }

  return addDefaultWebpExtension(path);
};

const normalizeR2Path = (path: string) => addDefaultWebpExtension(path);

const buildR2UrlFromPath = (manifest: Manifest, path: string) => {
  if (!path || typeof path !== 'string') return '';

  const normalizedPath = normalizeR2Path(path);
  const fileName = normalizedPath.split('/').pop() || '';
  const asset = manifest[fileName];

  if (asset) {
    const folder = asset.folder ? `${asset.folder}/` : '';
    return `${ASSET_BASE_URL}/${folder}${asset.hashedName}`;
  }

  const cleanPath = normalizedPath.startsWith('/')
    ? normalizedPath
    : `/${normalizedPath}`;

  return `${ASSET_BASE_URL}${cleanPath}`;
};

interface ResolvedImageData {
  src: string;
  srcSet: string | null;
}

const resolveImageData = (
  manifest: Manifest,
  inputPath: R2ImagePath,
): ResolvedImageData => {
  if (!inputPath) {
    return { src: '', srcSet: null };
  }

  let pathStr = '';

  if (typeof inputPath === 'string') {
    pathStr = inputPath;
  } else if (typeof inputPath === 'object' && inputPath !== null) {
    pathStr = inputPath.url || inputPath.path || '';
  }

  if (!pathStr || typeof pathStr !== 'string') {
    return { src: '', srcSet: null };
  }

  if (isFullUrl(pathStr)) {
    const baseNoTrail = ASSET_BASE_URL.replace(/\/+$/, '');

    if (pathStr.startsWith(baseNoTrail)) {
      const pathPart = pathStr.replace(baseNoTrail, '');
      const hashedFileName = pathPart.split('/').pop() || '';

      const manifestKey = hashedFileName.replace(/\.([a-f0-9]{12})\./, '.');

      if (
        manifestKey !== hashedFileName &&
        manifest[manifestKey]?.variants?.length
      ) {
        return {
          src: pathStr,
          srcSet: getAssetSrcSet(manifest, manifestKey),
        };
      }
    }

    return { src: pathStr, srcSet: null };
  }

  if (pathStr.includes('images')) {
    const fileName = pathStr.split('/').pop() || '';

    if (manifest[fileName]) {
      return {
        src: buildR2UrlFromPath(manifest, pathStr),
        srcSet: getAssetSrcSet(manifest, fileName),
      };
    }

    return { src: normalizePublicPath(pathStr), srcSet: null };
  }

  const normalized = normalizeR2Path(pathStr);
  const fileName = normalized.split('/').pop() || '';

  return {
    src: buildR2UrlFromPath(manifest, pathStr),
    srcSet: getAssetSrcSet(manifest, fileName),
  };
};

const isR2CdnUrl = (src: string): boolean => {
  if (!src.startsWith('https://')) return false;
  const baseNoTrail = ASSET_BASE_URL.replace(/\/+$/, '');
  return src.startsWith(baseNoTrail);
};

const DEFAULT_SIZES =
  '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw';

const FALLBACK_VIEWPORT_WIDTH = 1440;

const extractSizeValue = (segment: string): string => {
  const idx = segment.lastIndexOf(')');
  return idx >= 0 ? segment.slice(idx + 1).trim() : segment.trim();
};

const parseMaxSizeWidth = (sizesAttr: string): number => {
  if (!sizesAttr) return 0;

  const viewportWidth = FALLBACK_VIEWPORT_WIDTH;
  const segments = sizesAttr.split(',').map((s) => s.trim());
  let maxPx = 0;

  for (const segment of segments) {
    const sizeValue = extractSizeValue(segment);
    const pxMatch = sizeValue.match(/(\d+)px/);
    if (pxMatch) {
      maxPx = Math.max(maxPx, parseInt(pxMatch[1]));
      continue;
    }
    const vwMatch = sizeValue.match(/(\d+)vw/);
    if (vwMatch) {
      maxPx = Math.max(
        maxPx,
        Math.round((parseInt(vwMatch[1]) / 100) * viewportWidth),
      );
    }
  }

  return maxPx;
};

const parseMaxSrcSetWidth = (srcSetStr: string): number => {
  if (!srcSetStr) return 0;
  const matches = srcSetStr.match(/(\d+)w/g);
  if (!matches || matches.length === 0) return 0;
  return Math.max(...matches.map((m) => parseInt(m)));
};

const R2Image = ({
  path: inputPath,
  alt = '',
  className = '',
  containerClassName,
  sizes: sizesProp,
  priority,
  loading,
  style,
  width,
  height,
  onLoad,
  onError,
  objectFit,
  fetchPriority,
  unoptimized = false,
}: R2ImageProps) => {
  const manifest = useManifest();
  const { src: fullSrc, srcSet } = resolveImageData(manifest, inputPath);

  const sizes = sizesProp || DEFAULT_SIZES;

  const fallbackSrc = fullSrc || '/images/placeholder.webp';

  const isR2 = isR2CdnUrl(fullSrc);
  const hasSrcSet = !!srcSet && isR2;

  const maxSizeW = sizesProp && hasSrcSet ? parseMaxSizeWidth(sizesProp) : 0;
  const maxVariantW = hasSrcSet ? parseMaxSrcSetWidth(srcSet!) : 0;
  const srcSetAdequate =
    !hasSrcSet ||
    maxSizeW === 0 ||
    maxVariantW === 0 ||
    maxVariantW >= maxSizeW * 2;
  const loadingAttr = loading || (priority ? 'eager' : 'lazy');
  const fetchPriorityAttr = fetchPriority || (priority ? 'high' : undefined);
  const decodingAttr = priority ? 'sync' : ('async' as const);

  if (isR2 && srcSetAdequate) {
    const img = (
      <img
        src={fallbackSrc}
        alt={alt ?? ''}
        sizes={hasSrcSet ? sizes : undefined}
        srcSet={hasSrcSet ? srcSet! : undefined}
        className={className || 'object-cover'}
        loading={loadingAttr}
        fetchPriority={fetchPriorityAttr}
        style={style}
        width={width}
        height={height}
        onLoad={onLoad}
        onError={onError}
        decoding={decodingAttr}
      />
    );

    if (width && height) return img;

    return (
      <div
        className={
          containerClassName ?? 'relative h-full w-full overflow-hidden'
        }
      >
        <img
          src={fallbackSrc}
          alt={alt ?? ''}
          sizes={hasSrcSet ? sizes : undefined}
          srcSet={hasSrcSet ? srcSet! : undefined}
          className={`blur-0 pointer-events-none scale-100 grayscale-0 transition-all duration-700 ease-in-out ${className || 'object-cover'}`}
          loading={loadingAttr}
          fetchPriority={fetchPriorityAttr}
          style={{
            ...style,
            width: '100%',
            height: '100%',
            objectFit: objectFit ?? 'cover',
            position: 'absolute',
            inset: 0,
          }}
          onLoad={onLoad}
          onError={onError}
          decoding={decodingAttr}
        />
      </div>
    );
  }

  const Image = require('next/image').default;
  const hasFixedSize = !!width && !!height;

  if (hasFixedSize) {
    return (
      <Image
        src={fallbackSrc}
        alt={alt}
        width={width}
        height={height}
        sizes={sizes}
        className={className}
        style={style}
        priority={priority}
        loading={loading}
        unoptimized={unoptimized}
        onLoad={onLoad}
        onError={onError}
      />
    );
  }

  return (
    <div
      className={containerClassName ?? 'relative h-full w-full overflow-hidden'}
    >
      <Image
        src={fallbackSrc}
        alt={alt || ''}
        fill
        sizes={sizes}
        className={`blur-0 pointer-events-none scale-100 grayscale-0 transition-all duration-700 ease-in-out ${className || 'object-cover'}`}
        style={style}
        priority={priority}
        loading={loading}
        unoptimized={unoptimized}
        onLoad={onLoad}
        onError={onError}
      />
    </div>
  );
};

export default R2Image;
